// src/components/CurrentLoadsPage.js
import React, { useEffect, useState, useCallback, useMemo, lazy, Suspense } from 'react';
import { auth, db, functions } from '../firebase';
import { applyOwnerImpersonation } from '../utils/impersonation';
import { isPlainDispatcher } from '../utils/companyScope';
import { deriveLoadTeamId } from '../utils/loadTeam';
import { makeLoadAccess, makeDriverPickAccess, makeLeftTeamCheck, makeMovedTeamCheck, leftTeamAsMainAdmin } from '../utils/loadAccess';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { doc, getDoc } from "firebase/firestore";

// Context
import { useTimezone } from '../contexts/TimezoneContext';
import { timesInLabel } from '../utils/timezoneLabel';
import { httpsCallable } from 'firebase/functions';
import { useDispatcherAnnouncements } from '../hooks/useDispatcherAnnouncements';
import { getSettings as getVoiceSettings } from '../utils/announcer';

// Custom Hooks
import { useDropdownData } from './loads/hooks/useDropdownData';
import { useLoadsData } from './loads/hooks/useLoadsData';
import { useLoadForm } from './loads/hooks/useLoadForm';
import { useLoadActions } from './loads/hooks/useLoadActions';
import { useLoadsMap } from './loads/hooks/useLoadsMap';

// Utils
import { 
  canUserManageLoads, 
  canUserSeeDispatcherFilter,
  userHasAnyRole
} from './loads/utils/loadHelpers';
import { 
  getDefaultFilters,
  COMMODITY_TYPES 
} from './loads/utils/constants';
import {
  extractCityStateZip,
  formatDateOnly,
  formatTimestampForDisplay
} from './loads/utils/formatters';

// Components
import PDFupload from "./PDFupload";
import LoadDocuments from './LoadDocuments';

// Lazy load heavy components
const LoadsTable = lazy(() => import('./loads/components/LoadsTable'));
const LoadsFilters = lazy(() => import('./loads/components/LoadsFilters'));
const LoadModal = lazy(() => import('./loads/components/LoadModal'));
const DeleteConfirmationModal = lazy(() => import('./loads/components/DeleteConfirmationModal'));
const LoadsMapView = lazy(() => import('./loads/components/LoadsMapView'));

// ============================================================================
// IMPROVED LOADING COMPONENTS
// ============================================================================

/**
 * Main app loader with your logo
 * Shows during initial page load until all critical data is ready
 */
// REPLACE the AppLoader with this version:
const AppLoader = ({ message = "Loading..." }) => (
  <div className="max-w-full mx-auto py-4 px-1 sm:px-6 lg:px-8 bg-gray-50 min-h-screen">
    <style>{`
      @keyframes slideIn {
        from { opacity: 0; transform: translateY(8px); }
        to { opacity: 1; transform: translateY(0); }
      }
      .skeleton-row {
        opacity: 0;
        animation: slideIn 0.3s ease-out forwards;
      }
    `}</style>

    {/* Skeleton Header */}
    <div className="mb-6 skeleton-row" style={{animationDelay: '0ms'}}>
      <div className="flex justify-between items-center">
        <div>
          <div className="h-8 w-48 bg-gray-200 rounded animate-pulse"></div>
          <div className="h-3 w-64 bg-gray-200 rounded animate-pulse mt-2"></div>
        </div>
        <div className="flex gap-2">
          <div className="h-9 w-24 bg-blue-200 rounded animate-pulse"></div>
          <div className="h-9 w-32 bg-gray-200 rounded animate-pulse"></div>
        </div>
      </div>
    </div>

    {/* Skeleton Filters */}
    <div className="mb-4 flex gap-3 skeleton-row" style={{animationDelay: '100ms'}}>
      {[1,2,3,4].map(i => (
        <div key={i} className="h-9 w-36 bg-gray-200 rounded animate-pulse"></div>
      ))}
    </div>

    {/* Skeleton Table */}
    <div className="bg-white shadow-lg rounded-lg overflow-hidden">
      {/* Table Header */}
      <div className="bg-gray-100 border-b border-gray-200 px-4 py-3 flex gap-4 skeleton-row" style={{animationDelay: '200ms'}}>
        {[40, 80, 60, 100, 120, 80, 70, 60].map((w, i) => (
          <div key={i} className="h-3 bg-gray-300 rounded animate-pulse" style={{width: w}}></div>
        ))}
      </div>
      
      {/* Table Rows - staggered */}
      {[1,2,3,4,5,6,7,8,9,10,11,12,13,14].map(row => (
        <div 
          key={row} 
          className="px-4 py-4 border-b border-gray-100 flex gap-4 items-center skeleton-row"
          style={{animationDelay: `${200 + row * 120}ms`}}
        >
          <div className="h-4 w-6 bg-gray-200 rounded animate-pulse"></div>
          <div className="h-4 w-20 bg-blue-100 rounded animate-pulse"></div>
          <div className="h-6 w-6 bg-gray-200 rounded animate-pulse"></div>
          <div className="h-5 w-20 bg-green-100 rounded-full animate-pulse"></div>
          <div className="flex-1">
            <div className="h-3 w-32 bg-gray-200 rounded animate-pulse mb-1"></div>
            <div className="h-3 w-48 bg-gray-100 rounded animate-pulse"></div>
          </div>
          <div className="flex-1">
            <div className="h-3 w-28 bg-gray-200 rounded animate-pulse mb-1"></div>
            <div className="h-3 w-40 bg-gray-100 rounded animate-pulse"></div>
          </div>
          <div className="h-8 w-32 bg-gray-200 rounded animate-pulse"></div>
          <div className="h-4 w-16 bg-green-100 rounded animate-pulse"></div>
          <div className="h-3 w-20 bg-gray-200 rounded animate-pulse"></div>
        </div>
      ))}
    </div>

    {/* Loading indicator */}
    <div className="mt-4 flex items-center justify-center gap-2 text-sm text-gray-500 skeleton-row" style={{animationDelay: '1400ms'}}>
      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
      <span>{message}</span>
    </div>
  </div>
);

/**
 * Lightweight component loader for lazy-loaded components
 * Much less prominent than the main loader
 */
const ComponentLoader = () => (
  <div className="flex items-center justify-center p-4">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
  </div>
);

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function CurrentLoadsPage() {
  // Authentication state
  const [loggedInUser, setLoggedInUser] = useState(null);
  const [isAuthLoading, setIsAuthLoading] = useState(true);
  
  // Filter state
  const [filters, setFilters] = useState(getDefaultFilters());
  const [loadLimit, setLoadLimit] = useState(30);

  // PDF processing state
  const [pdfProcessingStatus, setPdfProcessingStatus] = useState('');
  
  const safeSetFilters = useCallback((newFilters) => {
    if (!newFilters) {
      console.error('Attempted to set undefined filters');
      setFilters(getDefaultFilters());
      return;
    }
    setFilters(newFilters);
  }, []);
  
  // Timezone context
  const { applicationTimeZone, isLoadingTimeZone } = useTimezone();

  // Permission checks (moved earlier to include in initial load check)
  const canManageLoads = canUserManageLoads(loggedInUser);
  const canSeeDispatcherFilter = canUserSeeDispatcherFilter(loggedInUser);

  // Custom hooks for data management
  const {
    drivers,
    allDrivers,
    trucks,
    dispatchers,
    allDispatchers,
    teamUsers,
    brokers,
    companies: tenantCompanies,
    commodityTypes,
    isAutomobileHauling,
    isDryVan,
    isReefer,
    isFlatbed,
    isTanker,
    isLoadingDropdowns
  } = useDropdownData(loggedInUser);


  // Every load carries its team (utils/loadTeam), worked out from these lookups.
  const teamLookup = useMemo(() => ({
    usersById: new Map((teamUsers || []).map(u => [u.id, u])),
    driversById: new Map((allDrivers || []).map(d => [d.id, d])),
  }), [teamUsers, allDrivers]);

  /**
   * The loads this viewer works with: the one rule in utils/loadAccess, then,
   * for a plain dispatcher, only their own part of their team's work — loads
   * they dispatch, loads of the drivers linked to them, and unassigned loads to
   * pick up. Applied before paging, inside useLoadsData.
   */
  const canSeeLoad = useMemo(() => {
    const allowed = makeLoadAccess(loggedInUser, teamLookup);
    if (!isPlainDispatcher(loggedInUser)) return allowed;

    const viewerId = loggedInUser?.uid || loggedInUser?.id || null;
    const mine = new Set(drivers
      .filter(d => !d.isDeleted && d.status === 'Active')
      .map(d => d.id));
    // ...and loads they were dispatching when the driver moved to another team.
    const movedFromMe = (load) => Boolean(viewerId) && Array.isArray(load.teamMoves) &&
      load.teamMoves.some(m => m?.fromDispatcherId === viewerId);
    return (load) => allowed(load) && (
      (viewerId && load.dispatcherId === viewerId) || !load.driverId || mine.has(load.driverId) ||
      movedFromMe(load)
    );
  }, [loggedInUser, teamLookup, drivers]);

  // The Empty Drivers widget used to sit here. It was removed because it was
  // the single most expensive thing on this page by a wide margin and nobody
  // used it: it ran two Firestore queries with no limit on them — every load in
  // a current status, and EVERY delivered load the tenant had ever had. On the
  // largest account that was 1,862 + 16,495 documents, around 455 MB, fetched
  // before a Super Admin or Main Admin could do anything. Load documents are
  // ~25 KB each, and 20 KB of that is pickup/delivery photo metadata the widget
  // never looked at.
  const {
    currentLoads,
    isLoading: isLoadingLoads,
    error: loadsError,
    totalLoads,
    filteredCount,
    displayedCount,
    hasMoreLoads,
    LOADS_PER_PAGE,
    MAX_LOADS
  } = useLoadsData(loggedInUser, filters, applicationTimeZone, isLoadingTimeZone, loadLimit, canSeeLoad);

  // ============================================================================
  // UNIFIED LOADING STATE - Key Improvement
  // ============================================================================
  
  /**
   * Determine if we should show the main app loader
   * Only show until ALL critical initial data is ready
   * Include Empty Drivers Widget in initial load
   */
  const isInitialLoading = isAuthLoading || isLoadingTimeZone || 
    (loggedInUser && (isLoadingDropdowns || (isLoadingLoads && currentLoads.length === 0)));

  /**
   * Show different loading messages based on what's loading
   */
  const getLoadingMessage = () => {
    if (isAuthLoading) return "Authenticating...";
    if (isLoadingTimeZone) return "Setting up timezone...";
    if (isLoadingDropdowns) return "Loading system data...";
    if (isLoadingLoads) return "Loading your loads...";
    return "Loading...";
  };

  // ============================================================================
  // PAGINATION HANDLERS
  // ============================================================================

  const handleLoadMore = useCallback(() => {
    setLoadLimit(prev => prev + LOADS_PER_PAGE);
  }, [LOADS_PER_PAGE]);

  useEffect(() => {
    setLoadLimit(30);
  }, [filters]);

  // ============================================================================
  // FILTER DRIVERS/TRUCKS
  // ============================================================================

  // Active drivers this viewer may put on a load: their team only, the same
  // boundary as the dispatcher list beside it (utils/loadAccess). A row whose
  // assigned driver is outside it still shows that driver, not selectable.
  const activeDrivers = useMemo(() => {
    const mayPick = makeDriverPickAccess(loggedInUser);
    return drivers.filter(driver =>
      !driver.isDeleted &&
      driver.status !== "Deleted" &&
      driver.status === "Active" &&
      mayPick(driver)
    );
  }, [drivers, loggedInUser]);

  // Whoever is already assigned to a load must stay in the row's dropdown even
  // if they've since been deactivated or moved to another company. Without this
  // the select renders blank and the load looks unassigned.
  /**
   * The drivers a user may CHOOSE.
   *
   * This used to add back every driver attached to any visible load, taken from
   * the unfiltered list — which quietly undid the dispatcher narrowing, since a
   * dispatcher can see plenty of loads belonging to other dispatchers' drivers.
   *
   * The reason for that add-back was real: a row whose driver is missing from
   * the list renders blank. That is now solved where it belongs, in the row
   * itself, which shows the assigned driver as a non-selectable entry.
   */

  // Which loads appear was decided before paging (canSeeLoad). Here each load
  // only gains its team, which the row uses to limit the dispatcher picker to
  // the load's own team.
  //
  // A plain dispatcher also sees their linked drivers' loads that a Main Admin
  // gave to another dispatcher. Those are marked dispatchedByOther: the row shows
  // who runs it now and is read-only.
  //
  // A load still visible only because of a team the viewer left carries
  // leftTeam ('manage' or 'view', as the Super Admin chose): the row says they
  // are no longer on the team, and with 'view' is read-only.
  //
  // A load moved to another team with its driver carries movedToTeam (that
  // team's id): the old team sees it as managed there, read-only.
  const loadsForViewer = useMemo(() => {
    const viewerId = loggedInUser?.uid || loggedInUser?.id || null;
    const plainDispatcher = isPlainDispatcher(loggedInUser);
    const leftTeamOf = makeLeftTeamCheck(loggedInUser, teamLookup);
    const movedTeamOf = makeMovedTeamCheck(loggedInUser, teamLookup);
    return currentLoads.map(load => {
      const movedToTeam = movedTeamOf(load);
      const teamKey = deriveLoadTeamId(load, teamLookup.usersById, teamLookup.driversById);
      const leftTeam = leftTeamOf(load);
      return {
        ...load,
        teamKey,
        movedToTeam,
        dispatchedByOther: !movedToTeam && plainDispatcher && Boolean(viewerId) &&
          Boolean(load.dispatcherId) && load.dispatcherId !== viewerId,
        leftTeam,
        leftTeamAsMainAdmin: Boolean(leftTeam) && leftTeamAsMainAdmin(loggedInUser, teamKey),
      };
    });
  }, [currentLoads, teamLookup, loggedInUser]);

  // Spoken alerts live here, below loadsForViewer, because they read it.
  // Placed above it originally, the dependency array referenced a const that
  // did not exist yet — a temporal dead zone error that throws during render
  // and takes the whole page down rather than degrading.
  // Town and state for the spoken alerts.
  //
  // Only for drivers who are actually idle — a driver who is rolling is not
  // going to be announced, so naming where they are would be paying for an
  // answer nothing says out loud. The server caches by ~5 km cell, so a fleet
  // sitting in one town costs a single lookup between them.
  const voiceEnabled = getVoiceSettings().enabled;
  const [driverPlaces, setDriverPlaces] = useState(() => new Map());

  const idleDriverPoints = useMemo(() => {
    if (!voiceEnabled) return [];
    const busy = new Set(
      loadsForViewer
        .filter(l => ['dispatched', 'in transit'].includes(String(l.status || '').toLowerCase()))
        .map(l => l.driverId)
    );
    return (drivers || [])
      .filter(d => !busy.has(d.id) &&
        typeof d.lastLocation?.lat === 'number' &&
        typeof d.lastLocation?.lng === 'number')
      .map(d => ({ id: d.id, lat: d.lastLocation.lat, lng: d.lastLocation.lng }));
  }, [drivers, loadsForViewer, voiceEnabled]);

  // Keyed on the rounded coordinates, not the driver list: a position ping
  // that moves a truck fifty metres must not re-run this.
  const placesKey = useMemo(
    () => idleDriverPoints
      .map(p => `${p.id}:${p.lat.toFixed(2)},${p.lng.toFixed(2)}`)
      .sort()
      .join('|'),
    [idleDriverPoints]
  );

  useEffect(() => {
    if (!voiceEnabled || idleDriverPoints.length === 0) return undefined;
    let cancelled = false;

    httpsCallable(functions, 'reverseGeocodePlaces')({ points: idleDriverPoints })
      .then(({ data }) => {
        if (cancelled) return;
        setDriverPlaces(new Map(Object.entries(data?.places || {})));
      })
      .catch(err => {
        // The announcement falls back to "James is empty" without the place.
        // Worth saying something imperfect rather than nothing.
        console.error('Could not resolve driver locations:', err?.message);
      });

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [placesKey, voiceEnabled]);

  // Spoken alerts. Driven from this page because it already holds the live
  // driver and load subscriptions — a second set of listeners somewhere else
  // would double the cost to say the same things.
  useDispatcherAnnouncements(drivers, loadsForViewer, {
    enabled: voiceEnabled,
    placeByDriverId: driverPlaces,
  });

  const driversForLoads = useMemo(
    () => [...activeDrivers].sort((a, b) => (a.name || '').localeCompare(b.name || '')),
    [activeDrivers]
  );

  const activeTrucks = useMemo(() => trucks.filter(truck => 
    !truck.isDeleted && 
    truck.status !== "Deleted" &&
    truck.status === "Active"
  ), [trucks]);

  // ============================================================================
  // FORM HOOKS
  // ============================================================================

  const {
    loadForm,
    setLoadForm,
    showLoadModal,
    setShowLoadModal,
    isEditing,
    setIsEditing,
    editDocId,
    setEditDocId,
    handleInputChange,
    handleVehicleChange,
    handleVehicleCountChange,
    resetForm,
    populateFormForEdit,
    populateFormFromPDF
  } = useLoadForm(isAutomobileHauling);

  const {
    handleStatusChange,
    handleDriverChange,
    handleTruckChange,
    handleBrokerChange,
    handleDispatcherChange,
    handleDeleteClick,
    handleDeleteLoad,
    handleLoadFormSubmit,
    showDeleteModal,
    setShowDeleteModal,
    loadToDelete,
    isProcessing,
    canManageLoads: canManage
  } = useLoadActions(loggedInUser, currentLoads, drivers, brokers);

  // Map view hook
  const {
    showMapView,
    toggleMapView,
    driverLocations,
    isLoadingMap,
    getFilteredLoadsForMap
  } = useLoadsMap(loggedInUser, filters);

  // ============================================================================
  // AUTHENTICATION EFFECT
  // ============================================================================

  useEffect(() => {
    console.log("Auth Effect: Running onAuthStateChanged setup.");
    const unsubscribeAuth = onAuthStateChanged(auth, async (user) => {
      console.log("Auth Effect: onAuthStateChanged callback fired. User:", user ? user.uid : 'null');
      if (user) {
        const userDocRef = doc(db, "users", user.uid);
        try {
          const docSnap = await getDoc(userDocRef);
          if (docSnap.exists()) {
            const userProfile = applyOwnerImpersonation({ uid: user.uid, email: user.email, ...docSnap.data() });
            console.log("Auth Effect: Profile fetched:", userProfile);
            setLoggedInUser(userProfile);
            if (userProfile.active === false) {
              alert("Your account has been disabled. Please contact an administrator.");
              signOut(auth).catch(err => console.error("Error during sign out for disabled account:", err));
            }
          } else {
            console.warn("Auth Effect: User profile not found for UID:", user.uid);
            setLoggedInUser({ uid: user.uid, email: user.email, role: null, active: false });
          }
        } catch (profileError) {
          console.error("Auth Effect: Error fetching user profile:", profileError);
          setLoggedInUser({ uid: user.uid, email: user.email, role: null, active: false });
        }
      } else {
        console.log("Auth Effect: No user signed in.");
        setLoggedInUser(null);
      }
      setIsAuthLoading(false);
      console.log("Auth Effect: isLoading set to false.");
    });
    
    return () => {
      console.log("Auth Effect: Cleaning up onAuthStateChanged.");
      unsubscribeAuth();
    };
  }, []);

  // ============================================================================
  // ESC KEY HANDLER
  // ============================================================================

  useEffect(() => {
    if (!showLoadModal && !showDeleteModal) return;

    const handleEscKey = (event) => {
      if (event.keyCode === 27) {
        if (showLoadModal) setShowLoadModal(false);
        if (showDeleteModal) setShowDeleteModal(false);
      }
    };

    document.addEventListener('keydown', handleEscKey);
    return () => document.removeEventListener('keydown', handleEscKey);
  }, [showLoadModal, showDeleteModal, setShowDeleteModal]);

  // ============================================================================
  // EVENT HANDLERS
  // ============================================================================

  const handleAddLoadClick = useCallback(() => {
    if (!canManageLoads) {
      alert("You don't have permission.");
      return;
    }
    resetForm();
    setLoadForm(prev => ({
      ...prev,
      // Same rule as a PDF import: the creator dispatches it if they dispatch
      // at all. A string comparison missed every Admin who also dispatches,
      // since their roles are stored as a list.
      dispatcherId: userHasAnyRole(loggedInUser, ['Dispatcher']) ? loggedInUser.uid : ""
    }));
    setShowLoadModal(true);
  }, [canManageLoads, resetForm, setLoadForm, loggedInUser, setShowLoadModal]);

  // A load kept only because of a team the viewer left ("See and manage") can
  // be finished, but not handed to a driver of their new team: the driver
  // list they pick from is their new team's, and choosing one would carry the
  // old team's load across. The driver stays as it is (LoadModal lockDriver).
  const [editDriverLocked, setEditDriverLocked] = useState(null);

  const handleEditLoadClick = useCallback((load) => {
    if (!canManageLoads) {
      alert("You don't have permission.");
      return;
    }
    setEditDriverLocked(load.leftTeam ? (load.driverId || '') : null);
    populateFormForEdit(load);
  }, [canManageLoads, populateFormForEdit]);

  const handleFilterChange = useCallback((e) => {
  const { name, value, type, checked } = e.target;
  
  if (name === 'all') {
    setFilters(value);
  } else if (name === 'single') {
    setFilters(value);
  } else if (type === 'checkbox') {
    // Handle checkbox filters
    setFilters(prev => ({ ...prev, [name]: checked }));
  } else {
    setFilters(prev => ({ ...prev, [name]: value }));
  }
}, []);

  const handlePdfProcessingComplete = useCallback(({ data: loadDataFromAI, error: processingError }) => {
    setPdfProcessingStatus('');
    if (processingError) {
      alert("PDF Processing Error: " + processingError);
      console.error("PDF Processing Error reported by PDFupload component:", processingError);
      return;
    }
    if (!loadDataFromAI) {
      alert("AI did not return any data from the PDF.");
      console.warn("No data received from PDF processing.");
      return;
    }
    populateFormFromPDF(loadDataFromAI, brokers, loggedInUser);

    // An auction gate pass carries the buyer's billing address, not the
    // destination, so the AI deliberately leaves delivery blank. Said plainly
    // here because an empty field on a form that just auto-filled everything
    // else looks like the import failed, rather than like a question.
    if (loadDataFromAI.needsManualDestination) {
      alert(
        'This looks like an auction gate pass (Manheim, Copart, IAA, ADESA or similar).\n\n' +
        'The pickup location has been filled in, but the DELIVERY ADDRESS has been ' +
        'left empty on purpose — the address printed on a gate pass is the buyer\'s ' +
        'billing address and is almost never where the vehicle actually goes.\n\n' +
        'Please enter the real delivery address before saving.'
      );
    }

    if (isAutomobileHauling && (loadDataFromAI.driverCollectionAmount > 0 || loadDataFromAI.brokerFeeCollection > 0)) {
      console.log("💰 Payment collection detected from PDF:", {
        driverCollection: loadDataFromAI.driverCollectionAmount,
        brokerFee: loadDataFromAI.brokerFeeCollection
      });
    }
  }, [populateFormFromPDF, brokers, loggedInUser, isAutomobileHauling]);

  const handleFormSubmit = useCallback(async (e) => {
    e.preventDefault();
    const result = await handleLoadFormSubmit(loadForm, isEditing, editDocId);
    if (result.success) {
      setShowLoadModal(false);
      resetForm();
    } else if (result.cancelled) {
      // Backed out of the cross-team confirmation. Not an error, and the form
      // stays open with their edits intact so they can change their mind.
    } else {
      alert("Error saving load: " + result.error);
    }
  }, [handleLoadFormSubmit, loadForm, isEditing, editDocId, setShowLoadModal, resetForm]);

  // ============================================================================
  // RENDER LOGIC - IMPROVED
  // ============================================================================

  // The count, in words. Bold numbers so the eye lands on them rather than on
  // the word "Showing".
  const loadCountLabel = useMemo(() => {
    const plural = (n) => (n !== 1 ? 's' : '');
    const n = (v) => <span className="font-semibold text-gray-800">{v}</span>;

    if (filters.searchLoadId && filters.searchLoadId.trim() !== '') {
      return <>{n(displayedCount)} matching load{plural(displayedCount)}</>;
    }
    if (displayedCount === filteredCount) {
      return <>All {n(displayedCount)} load{plural(displayedCount)}</>;
    }
    return (
      <>
        Showing {n(displayedCount)} of {n(filteredCount)} load{plural(filteredCount)}
        {filteredCount !== totalLoads && <span className="text-gray-400"> · {totalLoads} in total</span>}
      </>
    );
  }, [filters.searchLoadId, displayedCount, filteredCount, totalLoads]);

  // Show main app loader for initial loading
  if (isInitialLoading) {
    return <AppLoader message={getLoadingMessage()} />;
  }

  // Not logged in
  if (!loggedInUser) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center p-8 bg-white rounded-lg shadow-md">
          <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
          <p className="text-gray-600 text-lg">Please log in to view current loads.</p>
        </div>
      </div>
    );
  }

  // Inactive account
  if (loggedInUser.active === false) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center p-8 bg-red-50 rounded-lg shadow-md border-2 border-red-200">
          <svg className="w-16 h-16 text-red-500 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <h3 className="text-xl font-semibold text-red-800 mb-2">Account Inactive</h3>
          <p className="text-red-700">Your account is inactive. Please contact an administrator.</p>
        </div>
      </div>
    );
  }

  // ============================================================================
  // MAIN PAGE CONTENT
  // ============================================================================

  return (
    <div className="max-w-full mx-auto py-4 px-1 sm:px-6 lg:px-8">
      {/* Header */}
      <div className="mb-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div className="flex items-center gap-4">
            <div>
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-800">Current Loads</h2>
            </div>
            
            {/* Map View Toggle Button */}
            <button
              onClick={toggleMapView}
              className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${
                showMapView 
                  ? 'bg-blue-600 text-white hover:bg-blue-700' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-300'
              }`}
            >
              <svg 
                xmlns="http://www.w3.org/2000/svg" 
                className="w-5 h-5" 
                fill="none" 
                viewBox="0 0 24 24" 
                stroke="currentColor"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={2} 
                  d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" 
                />
              </svg>
              {showMapView ? 'Hide Map' : 'Map View'}
            </button>
          </div>
          
          <div className="flex gap-2 items-center flex-wrap">
            {canManageLoads && (
              <>
                <button 
                  onClick={handleAddLoadClick} 
                  className="bg-blue-500 hover:bg-blue-600 text-white font-medium py-2 px-4 rounded-md text-sm flex items-center shadow-sm transition-colors"
                  disabled={isProcessing}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4"/>
                  </svg>
                  Add Load
                </button>
                
                <PDFupload
                  onProcessingComplete={handlePdfProcessingComplete}
                  onUploadStart={() => setPdfProcessingStatus("Processing PDF...")}
                  onUploadError={(errMsg) => {
                    setPdfProcessingStatus('');
                    alert("Upload Error: " + errMsg);
                  }}
                  loggedInUser={loggedInUser}
                  disabled={isProcessing}
                />
              </>
            )}
            {pdfProcessingStatus && (
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                <span>{pdfProcessingStatus}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Error display */}
      {loadsError && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 flex items-start gap-3">
          <svg className="w-5 h-5 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div>
            <p className="font-medium">Error loading loads</p>
            <p className="text-sm mt-1">{loadsError}</p>
          </div>
        </div>
      )}

      {/* Map View */}
      {showMapView && (
        <div className="mb-6">
          <Suspense fallback={
            <div className="bg-white rounded-lg shadow-lg p-8">
              <div className="flex items-center justify-center">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                  <p className="mt-4 text-gray-600">Loading map...</p>
                </div>
              </div>
            </div>
          }>
            <div className="bg-white rounded-lg shadow-lg overflow-hidden">
              <div className="p-4 bg-gray-50 border-b border-gray-200">
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-semibold text-gray-800">Loads Map View</h3>
                  <div className="text-sm text-gray-600">
                    <span className="inline-flex items-center gap-2">
                      <span className="flex items-center gap-1">
                        <span className="w-3 h-3 bg-green-500 rounded-full"></span> 
                      </span>
                      <span className="flex items-center gap-1">
                        <span className="w-3 h-3 bg-blue-500 rounded-full"></span> 
                      </span>
                      <span className="flex items-center gap-1">
                        <span className="w-3 h-3 bg-orange-500 rounded-full"></span> 
                      </span>
                      <span className="flex items-center gap-1">
                        <span className="w-3 h-3 bg-gray-500 rounded-full"></span> 
                      </span>
                    </span>
                  </div>
                </div>
              </div>
              <LoadsMapView
                // Clicking a driver on the map sets the page's driver filter, so
                // the table below shows the same driver's work rather than
                // silently disagreeing with the map.
                onDriverFilterChange={(driverId) =>
                  setFilters(prev => ({ ...prev, driverId: driverId || 'all' }))}
                loads={loadsForViewer}
                drivers={drivers}
                trucks={trucks}
                brokers={brokers}
                isAutomobileHauling={isAutomobileHauling}
                googleMapsApiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}
                formatDateOnly={formatDateOnly}
                extractCityStateZip={extractCityStateZip}
                applicationTimeZone={applicationTimeZone}
                driverLocations={driverLocations}
              />
            </div>
          </Suspense>
        </div>
      )}

      {/* Filters */}
      <Suspense fallback={<ComponentLoader />}>
        <LoadsFilters
          filters={filters}
          onFilterChange={handleFilterChange}
          drivers={driversForLoads}
          trucks={activeTrucks}
          brokers={brokers}
          dispatchers={dispatchers}
          canSeeDispatcherFilter={canSeeDispatcherFilter}
        />
      </Suspense>

      {/* The row count and the timezone, directly above the rows they describe.
          They used to sit under the page title as one grey run-on line —
          "Showing 30 of 48 loads • Times displayed in: America/New_York" —
          where the count was nowhere near the table it counted and the zone was
          written the way a database stores it rather than the way a dispatcher
          says it. */}
      <div className="flex flex-wrap items-center justify-between gap-2 mt-4 mb-1.5 px-1">
        <p className="text-xs text-gray-600">
          {totalLoads > 0 ? loadCountLabel : <span className="text-gray-400">No loads to show</span>}
        </p>
        <span
          className="inline-flex items-center gap-1.5 text-[11px] text-gray-500 bg-gray-100
                     border border-gray-200 rounded-full px-2.5 py-1 whitespace-nowrap"
          title={applicationTimeZone ? `All times shown in ${applicationTimeZone}` : undefined}
        >
          <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <circle cx="12" cy="12" r="9" />
            <path strokeLinecap="round" d="M12 7v5l3 2" />
          </svg>
          {timesInLabel(applicationTimeZone)}
        </span>
      </div>

      {/* Table */}
      <Suspense fallback={<ComponentLoader />}>
        <LoadsTable
          loads={loadsForViewer}
          drivers={driversForLoads}
          driverLookup={allDrivers}
          companies={tenantCompanies}
          trucks={trucks}
          brokers={brokers}
          dispatchers={dispatchers}
          allDispatchers={allDispatchers}
          isAutomobileHauling={isAutomobileHauling}
          canManageLoads={canManageLoads}
          canSeeDispatcherFilter={canSeeDispatcherFilter}
          onStatusChange={handleStatusChange}
          onDriverChange={handleDriverChange}
          onTruckChange={handleTruckChange}
          onBrokerChange={handleBrokerChange}
          onDispatcherChange={handleDispatcherChange}
          onEdit={handleEditLoadClick}
          onDelete={handleDeleteClick}
          formatDateOnly={formatDateOnly}
          formatTimestampForDisplay={formatTimestampForDisplay}
          extractCityStateZip={extractCityStateZip}
          applicationTimeZone={applicationTimeZone}
          isLoading={false} // Never show table skeleton after initial load
          loggedInUser={loggedInUser}
          LoadDocuments={LoadDocuments}
        />
      </Suspense>

      {/* Load More Button */}
      {hasMoreLoads && !filters.searchLoadId && (
        <div className="mt-6 flex justify-center">
          <button
            onClick={handleLoadMore}
            className="bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-3 px-6 rounded-lg border border-gray-300 transition-colors flex items-center gap-2"
            disabled={isLoadingLoads}
          >
            {isLoadingLoads ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600"></div>
                Loading...
              </>
            ) : (
              <>
                <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
                Load More ({filteredCount - displayedCount} remaining)
              </>
            )}
          </button>
        </div>
      )}

      {/* Load Modal */}
      {showLoadModal && (
        <Suspense fallback={<ComponentLoader />}>
          <LoadModal
            isEditing={isEditing}
            loadForm={loadForm}
            onInputChange={handleInputChange}
            onSubmit={handleFormSubmit}
            onClose={() => {
              setShowLoadModal(false);
              setEditDriverLocked(null);
              resetForm();
            }}
            brokers={brokers}
            drivers={isEditing && editDriverLocked !== null
              ? (allDrivers || []).filter(d => d.id === editDriverLocked)
              : activeDrivers}
            lockDriver={isEditing && editDriverLocked !== null}
            trucks={activeTrucks}
            dispatchers={dispatchers}
            loggedInUser={loggedInUser}
            tenantCompanies={tenantCompanies}
            isLoadingCompanies={isLoadingDropdowns}
            isAutomobileHauling={isAutomobileHauling}
            isDryVan={isDryVan}
            isReefer={isReefer}
            isFlatbed={isFlatbed}
            isTanker={isTanker}
            onVehicleChange={handleVehicleChange}
            onVehicleCountChange={handleVehicleCountChange}
            setLoadForm={setLoadForm}
            isProcessing={isProcessing}
          />
        </Suspense>
      )}

      {/* Delete Modal */}
      {showDeleteModal && (
        <Suspense fallback={null}>
          <DeleteConfirmationModal
            isOpen={showDeleteModal}
            onClose={() => {
              setShowDeleteModal(false);
            }}
            onConfirm={handleDeleteLoad}
            loadId={currentLoads.find(l => l.docId === loadToDelete)?.load_id || loadToDelete}
            isProcessing={isProcessing}
          />
        </Suspense>
      )}
    </div>
  );
}